
<template>
  <main class="talmud-give">
    <div class="form-container ou-donation-form">
      <PaymentForm class="form-horizontal donation-form">
        <!-- <pre v-if="isDev" class="form-debug">{{ form }}</pre> -->
        <div class="tabs" role="tablist" aria-label="Gift type">
          <button
            v-for="gift in giftTypes"
            :key="gift.id"
            type="button"
            class="tab"
            :class="{ active: activeGift === gift.id }"
            @click="selectGift(gift.id)"
          >
            {{ gift.label }}
            <span class="amt">{{ gift.amountLabel }}</span>
          </button>
        </div>

        <section v-if="activeGift === 'general'" class="field-group amount-group">
          <div class="section-label">A gift of any amount</div>
          <p class="helper">Every gift keeps Talmud Daily free, with no paywall and no barrier to the learning.</p>
          <AmountSelector defaultCheckedAount="360"/>
          <ErrorLabel field="amount" />
        </section>

        <section v-if="activeGift === 'day'" class="field-group">
          <div class="section-label">Choose your day</div>
          <p class="helper">Pick any date in the forty-week journey. Jewish holidays are skipped — those dates aren't shown below. Days already sponsored are greyed out.</p>
          <select id="day-picker" class="day-select" :value="sponsorshipDaySlug" @change="selectSponsorshipDate">
            <option value="" disabled>Select a date…</option>
            <optgroup v-for="grp in dayGroups" :key="grp.weekNumber" :label="grp.label">
              <option
                v-for="day in grp.days"
                :key="day.daySlug"
                :value="day.daySlug"
                :disabled="isDayTaken(day)"
              >{{ day.label }}{{ isDayTaken(day) ? ' — Taken' : '' }}</option>
            </optgroup>
          </select>
        </section>

        <section v-if="activeGift === 'tractate'" class="field-group">
          <div class="section-label">Choose your tractate</div>
          <p class="helper">A full week — five daily episodes plus the Friday interview. Your dedication anchors the entire week's learning. Weeks already sponsored are greyed out.</p>
          <select id="tractate-picker" class="day-select" :value="sponsorshipWeekSlug" @change="selectSponsorshipTractate">
            <option value="" disabled>Select a tractate…</option>
            <optgroup v-for="grp in weekGroups" :key="grp.seder" :label="grp.seder">
              <option
                v-for="week in grp.weeks"
                :key="week.weekSlug"
                :value="week.weekSlug"
                :disabled="isWeekTaken(week)"
              >{{ week.label }}{{ isWeekTaken(week) ? ' — Taken' : '' }}</option>
            </optgroup>
          </select>
        </section>

        <section class="field-group">
          <div class="section-label">Dedication</div>
          <p class="helper">
            {{ activeGift === 'day'
              ? 'Sponsor this day in honor of, or in memory of, someone. Optional.'
              : 'Attach your gift to a name or a memory. Optional.' }}
          </p>
          <div class="choice-group">
            <button
              v-for="choice in dedicationChoices"
              :key="choice.id"
              type="button"
              class="choice"
              :class="{ active: dedicationType === choice.id }"
              @click="selectDedication(choice.id)"
            >
              {{ choice.label }}
            </button>
          </div>
          <FormInput
            v-if="dedicationType !== 'none'"
            class="dedication-input"
            field="custom.dedication"
            :placeholder="dedicationType === 'honor' ? 'In honor of...' : 'In memory of...'"
          />
        </section>

        <section v-if="activeGift !== 'general'" class="field-group">
          <div class="section-label">How should you be listed?</div>
          <p class="helper">This is how the sponsor line appears on the site and in the daily email.</p>
          <FormInput field="custom.listingName" placeholder="e.g. The Smith Family, or John & Sarah Cohen"  v-if="getValue('custom.anonymous') !== 'yes'" />
          <label class="anon-toggle" for="anonymousCheck">
            <input
              id="anonymousCheck"
              type="checkbox"
              :checked="getValue('custom.anonymous') === 'yes'"
              @click="setValue('custom.anonymous', $event.target.checked ? 'yes' : null)"
            />
            <span>List this gift anonymously instead</span>
          </label>
        </section>

        <section v-if="activeGift !== 'general'" class="field-group">
          <div class="section-label">Amount</div>
          <div class="fixed-amount">
            <span class="amt">{{ formattedAmount }}</span>
            <span class="amt-note">
              {{ activeGift === 'day'
                ? 'A day of learning — every episode of Talmud Daily on this date carries your dedication.'
                : 'A complete tractate sponsorship supporting the full program.' }}
            </span>
          </div>
        </section>

        <FormSection title="Your info" class="field-group">
          <FormRow inline>
            <FormCol field="title" :flex="1" />
            <FormCol field="firstName" :flex="2" />
            <FormCol field="lastName" :flex="2" />
          </FormRow>
          <FormRow inline>
            <FormCol field="email" />
            <FormCol field="phone" />
          </FormRow>
          <FormRow inline>
            <Consent privacy="https://ncsy.org/privacy-policy/" />
          </FormRow>
        </FormSection>

        <FormSection title="Billing information" class="field-group">
          <DonorAddress />
        </FormSection>

        <section class="field-group">
          <div class="section-label">Notes</div>
          <FormInput type="textarea" field="custom.Notes" placeholder="Anything else you'd like us to know?" />
        </section>

        <FormSection title="Payment information" class="field-group payment-section" last>
          <FormRow inline>
            <FormCol field="card" :showLabel="false" />
          </FormRow>
        </FormSection>

        <div class="submit-row">
          <p class="submit-note">Review your gift, then continue to secure payment.</p>
          <PaymentButton showAmount btnText="Donate" />
        </div>
      </PaymentForm>
    </div>

    <aside class="preview-col">
      <div class="preview">
        <div class="p-eyebrow">Preview</div>
        <h2 class="p-title">{{ activeGift === 'general' ? 'Your gift.' : 'How your gift will appear.' }}</h2>
        <p class="p-sub">
          {{ activeGift === 'general'
            ? 'General donations stay private'
            : 'On the episode page · in the daily email · on the archive' }}
        </p>
        <div v-if="activeGift !== 'general'" class="p-panel">
          <div class="p-panel-label">
            {{ activeGift === 'day' ? sponsorshipDayLabel : activeGift === 'tractate' ? sponsorshipWeekLabel : 'Talmud Daily' }}
          </div>
          <p class="p-line">Sponsored by <em>{{ donorName }}</em>.</p>
          <p v-if="dedicationText" id="p-dedication" class="p-dedication">
            {{ dedicationType === 'honor' ? 'In honor of' : 'In memory of' }} <em>{{ dedicationText }}</em>.
          </p>
        </div>
        <p class="p-note">
          {{ activeGift === 'general'
            ? "General gifts aren't listed publicly. Only day and tractate sponsorships appear on the site and in the daily email."
            : 'Add a dedication above to see it appear here — "in memory of…" or "in honor of…". Check the anonymous option to see how that reads.' }}
        </p>
        <div class="amt-summary">
          <span>Your gift</span>
          <span class="total">{{ formattedAmount }}</span>
        </div>
      </div>
    </aside>
  </main>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch
} from 'vue'
import { usePaymentForm } from '~/shared/use-payment-form'
import { TD_DAYS, TD_WEEKS, type TdDayOption, type TdWeekOption } from './talmuddaily-calendar'

export default defineComponent({
  setup() {
    const { getValue, setValue, form } = usePaymentForm()
    const isDev = import.meta.dev

    const activeGift = ref<'day' | 'tractate' | 'general'>('day')
    const dedicationType = ref<'none' | 'honor' | 'memory'>('none')

    // Selected sponsor-able day / week, keyed by stable slug.
    const sponsorshipDaySlug = ref('')
    const sponsorshipWeekSlug = ref('')

    // Already-sponsored keys fetched from the availability endpoint.
    const takenDays = ref<Set<string>>(new Set())   // keyed by ISO date
    const takenWeeks = ref<Set<number>>(new Set())  // keyed by program-week number

    const isDayTaken = (day: TdDayOption) => takenDays.value.has(day.dateISO)
    const isWeekTaken = (week: TdWeekOption) => takenWeeks.value.has(week.weekNumber)

    // Group days by program week for the <optgroup> layout.
    const dayGroups = computed(() => {
      const byWeek = new Map<number, TdDayOption[]>()
      for (const day of TD_DAYS) {
        if (!byWeek.has(day.weekNumber)) byWeek.set(day.weekNumber, [])
        byWeek.get(day.weekNumber)!.push(day)
      }
      return Array.from(byWeek, ([weekNumber, days]) => {
        const week = TD_WEEKS.find(w => w.weekNumber === weekNumber)
        return {
          weekNumber,
          label: week ? `Week ${weekNumber} · ${week.tractate}` : `Week ${weekNumber}`,
          days,
        }
      })
    })

    // Group weeks by seder for the <optgroup> layout.
    const weekGroups = computed(() => {
      const groups: { seder: string; weeks: TdWeekOption[] }[] = []
      for (const week of TD_WEEKS) {
        const seder = week.seder || 'Program'
        let group = groups.find(g => g.seder === seder)
        if (!group) { group = { seder, weeks: [] }; groups.push(group) }
        group.weeks.push(week)
      }
      return groups
    })

    const selectedDay = computed(() => TD_DAYS.find(d => d.daySlug === sponsorshipDaySlug.value) || null)
    const selectedWeek = computed(() => TD_WEEKS.find(w => w.weekSlug === sponsorshipWeekSlug.value) || null)
    const sponsorshipDayLabel = computed(() => selectedDay.value?.label || 'Select a date…')
    const sponsorshipWeekLabel = computed(() => selectedWeek.value?.label || 'Select a tractate…')

    const firstAvailableDay = () => TD_DAYS.find(d => !isDayTaken(d)) || TD_DAYS[0]
    const firstAvailableWeek = () => TD_WEEKS.find(w => !isWeekTaken(w)) || TD_WEEKS[0]

    const giftTypes = [
      { id: 'day' as const, label: 'Sponsor a day', amountLabel: '$360' },
      { id: 'tractate' as const, label: 'Sponsor a tractate', amountLabel: '$1,800' },
      { id: 'general' as const, label: 'Give any amount', amountLabel: 'Your choice' },
    ]

    const dedicationChoices = [
      { id: 'none' as const, label: 'No dedication' },
      { id: 'honor' as const, label: 'In honor of' },
      { id: 'memory' as const, label: 'In memory of' },
    ]

    const formattedAmount = computed(() => {
      const currency = form.value.currency || 'USD'
      const amount = Number(form.value.amount || 0)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amount)
    })

    const donorName = computed(() => {
      if (getValue('custom.anonymous') === 'yes') return 'Anonymous'
      const sponsorName = form.value.custom?.listingName
      if (sponsorName) return sponsorName
      const name = [form.value.firstName, form.value.lastName].filter(Boolean).join(' ')
      return name || '[Your Name]'
    })

    const dedicationText = computed(() => {
      if (dedicationType.value === 'none') return ''
      const dedication = form.value.custom?.dedication
      if (!dedication) return ''
      return dedication
    })

    // Write the stable + display keys for the selected day/week onto the form.
    const applyDaySelection = () => {
      const day = selectedDay.value
      setValue('custom.date', day ? day.label : null)
      setValue('custom.dateISO', day ? day.dateISO : null)
      setValue('custom.daySlug', day ? day.daySlug : null)
    }
    const applyWeekSelection = () => {
      const week = selectedWeek.value
      setValue('custom.seriesType', week ? week.label : null)
      setValue('custom.weekSlug', week ? week.weekSlug : null)
      setValue('custom.weekNumber', week ? String(week.weekNumber) : null)
    }

    const selectGift = (gift: 'day' | 'tractate' | 'general') => {
      activeGift.value = gift
      setValue('amount', gift === 'day' ? '360' : gift === 'tractate' ? '1800' : '360')
      setValue('custom.sponsorshipType', gift === 'day' ? 'Sponsor a Day' : gift === 'tractate' ? 'Sponsor a Tractate' : 'General Donation')

      if (gift === 'day') {
        const cur = selectedDay.value
        if (!cur || isDayTaken(cur)) sponsorshipDaySlug.value = firstAvailableDay().daySlug
        applyDaySelection()
        setValue('custom.seriesType', null)
        setValue('custom.weekSlug', null)
        setValue('custom.weekNumber', null)
      } else if (gift === 'tractate') {
        const cur = selectedWeek.value
        if (!cur || isWeekTaken(cur)) sponsorshipWeekSlug.value = firstAvailableWeek().weekSlug
        applyWeekSelection()
        setValue('custom.date', null)
        setValue('custom.dateISO', null)
        setValue('custom.daySlug', null)
      } else {
        setValue('custom.date', null)
        setValue('custom.dateISO', null)
        setValue('custom.daySlug', null)
        setValue('custom.seriesType', null)
        setValue('custom.weekSlug', null)
        setValue('custom.weekNumber', null)
        setValue('custom.listingName', null)
        setValue('custom.anonymous', null)
      }
    }

    const selectDedication = (type: 'none' | 'honor' | 'memory') => {
      dedicationType.value = type
      setValue('custom.dedicationType', type === 'honor' ? 'In honor of' : type === 'memory' ? 'In memory of' : null)
      if (type === 'none') setValue('custom.dedication', null)
    }

    const selectSponsorshipDate = (event: Event) => {
      sponsorshipDaySlug.value = (event.target as HTMLSelectElement).value
      applyDaySelection()
    }

    const selectSponsorshipTractate = (event: Event) => {
      sponsorshipWeekSlug.value = (event.target as HTMLSelectElement).value
      applyWeekSelection()
    }

    watch(
      () => form.value.amount,
      // The "other amount" input is type=number, so a typed amount arrives as a number;
      // customValidations requires a string, and a number silently blocks submit.
      amount => setValue('custom.sponsorshipAmount', amount === null || amount === undefined || amount === '' ? null : String(amount)),
      { immediate: true }
    )

    selectGift('day')

    // Best-effort availability: grey out days/weeks that are already sponsored.
    // Hit the OU donation API directly with an absolute URL, chosen by
    // environment, so this works when the form is embedded as a web component on
    // another domain (a relative `$fetch('/api/…')` resolves to the host page and
    // 404s / is undefined in the web-component build, leaving every day open).
    // The container sets window.isProduction from the resolved runtime.
    onMounted(async () => {
      try {
        const isProduction =
          typeof window !== 'undefined' &&
          ((window as any).isProduction === true ||
            String((window as any).__PAYMENT_FORM_API_URL__ || '').includes('v2.donateou.org'))
        const availabilityUrl = isProduction
          ? 'https://v2.donateou.org/api/talmuddaily-availability'
          : 'https://donate-v2.oudev.org/api/talmuddaily-availability'
        const res = await fetch(availabilityUrl)
        if (!res.ok) return
        const data: any = await res.json()
        if (data?.days) takenDays.value = new Set(Object.keys(data.days))
        if (data?.weeks) takenWeeks.value = new Set(Object.keys(data.weeks).map(Number))
        // If our default landed on a now-taken slot, advance to the next open one.
        if (activeGift.value === 'day' && selectedDay.value && isDayTaken(selectedDay.value)) selectGift('day')
        if (activeGift.value === 'tractate' && selectedWeek.value && isWeekTaken(selectedWeek.value)) selectGift('tractate')
      } catch (e) {
        // Endpoint unavailable — leave every option selectable.
      }
    })

    return {
      activeGift,
      dayGroups,
      dedicationChoices,
      dedicationText,
      dedicationType,
      donorName,
      form,
      formattedAmount,
      getValue,
      giftTypes,
      isDayTaken,
      isDev,
      isWeekTaken,
      selectDedication,
      selectGift,
      selectSponsorshipDate,
      selectSponsorshipTractate,
      setValue,
      sponsorshipDayLabel,
      sponsorshipDaySlug,
      sponsorshipWeekLabel,
      sponsorshipWeekSlug,
      weekGroups,
    }
  },
})
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');

.talmud-give {
  --ink: #172020;
  --paper: #eae2d0;
  --paper-pure: #f2eddf;
  --burgundy: #500e0f;
  --muted: #5e5c50;
  --rule: #c9bfa6;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 1fr);
  gap: 72px;
  align-items: start;
  max-width: 1240px;
  margin: 0 auto;
  padding: 64px 48px 96px;
  color: var(--ink);
  background: var(--paper);
  font-family: 'Hind', sans-serif;
  font-size: 17px;
  line-height: 1.55;
}

.talmud-give,
.talmud-give * {
  box-sizing: border-box;
}

.form-debug {
  max-height: 320px;
  margin: 0 0 24px;
  padding: 12px;
  overflow: auto;
  border-radius: 2px;
  background: var(--ink);
  color: var(--paper-pure);
  font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: pre-wrap;
  word-break: break-word;
}

.tabs {
  display: flex;
  margin-bottom: 40px;
  border-bottom: 1px solid var(--rule);
}

.tab {
  position: relative;
  margin-right: 40px;
  padding: 18px 4px;
  border: 0;
  background: transparent;
  color: var(--muted);
  font: 600 13px/1.3 'Hind', sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: pointer;
}

.tab.active {
  color: var(--burgundy);
}

.tab.active::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--burgundy);
  content: '';
}

.tab .amt {
  display: block;
  margin-top: 4px;
  color: inherit;
  font: italic 20px/1.2 'Instrument Serif', Georgia, serif;
  letter-spacing: 0;
  text-transform: none;
}

.field-group {
  margin-bottom: 32px;
}
:deep(.field-group) {
  label{
    padding-bottom: 0px;
    margin-bottom: 0px;
  }
}

.section-label,
:deep(.field-group > h2) {
  margin: 0 0 10px;
  color: var(--burgundy);
  font: 600 11px/1.4 'Hind', sans-serif;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

.helper {
  margin: 0 0 14px;
  color: var(--muted);
  font: italic 15px/1.4 'Instrument Serif', Georgia, serif;
}

.fixed-amount {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  padding: 20px 24px;
  border: 1px solid var(--rule);
  border-left: 3px solid var(--burgundy);
  background: var(--paper-pure);
}

.fixed-amount .amt {
  flex: none;
  color: var(--burgundy);
  font: 40px/1 'Instrument Serif', Georgia, serif;
}

.amt-note {
  color: var(--muted);
  font: italic 15px/1.35 'Instrument Serif', Georgia, serif;
}

.choice-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.choice {
  padding: 12px 18px;
  border: 1px solid var(--rule);
  border-radius: 2px;
  background: transparent;
  color: var(--muted);
  font: 500 14px/1.4 'Hind', sans-serif;
  cursor: pointer;
}

.choice:hover,
.choice.active {
  border-color: var(--burgundy);
  color: var(--burgundy);
}

.choice.active {
  background: var(--burgundy);
  color: var(--paper);
}

:deep(.dedication-input) {
  margin-top: 12px;
}

.anon-toggle {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
  color: var(--muted);
  font: 13px/1.4 'Hind', sans-serif;
  cursor: pointer;
  user-select: none;
}

.anon-toggle:hover {
  color: var(--burgundy);
}

.anon-toggle input[type='checkbox'] {
  width: 16px !important;
  height: 16px !important;
  margin: 0;
  padding: 0;
  accent-color: var(--burgundy);
  cursor: pointer;
}

:deep(.field-group input),
:deep(.field-group textarea),
:deep(.field-group select),
:deep(.field-group .vs__dropdown-toggle) {
  border-color: var(--rule) !important;
  border-radius: 1px !important;
  background: var(--paper-pure) !important;
}
:deep(.field-group textarea){
  padding: 8px 12px !important;
  font-style: inherit !important;
}


:deep(.field-group input:focus),
:deep(.field-group textarea:focus),
:deep(.field-group select:focus) {
  border-color: var(--ink) !important;
  box-shadow: none !important;
}

:deep(.amount-group .styled-radio--ou-donation label) {
  border-color: var(--rule);
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font: 20px/1.2 'Instrument Serif', Georgia, serif;
}

:deep(.amount-group .styled-radio--ou-donation input[type='radio']:checked + label) {
  border-color: var(--burgundy);
  background: var(--burgundy);
  color: var(--paper);
}

.payment-section {
  margin-bottom: 0;
}

.submit-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  margin-top: 0px;
  padding-top: 24px;
  border-top: 1px solid var(--rule);
}

.submit-note {
  max-width: 30ch;
  margin: 0;
  color: var(--muted);
  font: italic 14px/1.4 'Instrument Serif', Georgia, serif;
}

:deep(.submit-row .payment) {
  margin: 0;
}

:deep(.submit-row .ou-donation-btn) {
  min-height: 54px;
  padding: 16px 28px;
  border: 0;
  border-radius: 1px;
  background: var(--burgundy);
  color: var(--paper);
  font: 600 14px/1.2 'Hind', sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.preview-col {
  position: sticky;
  top: 96px;
}

.preview {
  padding: 32px 28px;
  border: 1px solid var(--rule);
  background: var(--paper-pure);
}

.p-eyebrow,
.p-panel-label {
  margin-bottom: 12px;
  color: var(--burgundy);
  font: 600 10px/1.4 'Hind', sans-serif;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

.p-title {
  margin: 0 0 4px;
  color: var(--ink);
  font: 22px/1.2 'Instrument Serif', Georgia, serif;
}

.p-sub {
  margin: 0 0 26px;
  color: var(--muted);
  font: 500 12px/1.4 'Hind', sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.p-panel {
  margin-bottom: 18px;
  padding: 18px 22px;
  border-left: 3px solid var(--burgundy);
  background: var(--paper);
}

.p-panel-label {
  margin-bottom: 8px;
  letter-spacing: 0.24em;
}

.p-line,
.p-dedication,
.p-note {
  margin: 0;
  color: var(--muted);
  font-family: 'Instrument Serif', Georgia, serif;
  font-style: italic;
}

.p-line {
  color: var(--ink);
  font-size: 18px;
  line-height: 1.35;
}

.p-line em,
.p-dedication em {
  color: var(--burgundy);
}

.p-dedication {
  margin-top: 4px;
  font-size: 15px;
}

.p-note {
  font-size: 13px;
  line-height: 1.4;
}

.amt-summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--rule);
  color: var(--muted);
  font: 600 12px/1.4 'Hind', sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.amt-summary .total {
  color: var(--burgundy);
  font: 32px/1 'Instrument Serif', Georgia, serif;
  letter-spacing: 0;
  text-transform: none;
}

@media (max-width: 900px) {
  .talmud-give {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 40px 24px 64px;
  }

  .preview-col {
    position: static;
  }

  .tabs {
    // flex-wrap: wrap;
  }

  .tab {
    margin-right: 16px;
    padding: 14px 4px;
  }
}

@media (max-width: 560px) {

  .tabs{
      gap: 5px;
    flex-flow: nowrap;
  }
  .talmud-give {
    padding-right: 18px;
    padding-left: 18px;
  }

  .tab {
    // width: 100%;
    margin-right: 0;
    // text-align: left;
  }

  .fixed-amount,
  .submit-row {
    align-items: flex-start;
    flex-direction: column;
  }
}

:deep(.field-group){
  div.vue-tel-input .vti__dropdown {
    background: var(--paper-pure) !important;
  }
} 

:deep(.styled-radio--ou-donation .amount_other__wrapper.USD):before {
    content: "$";
    font-family: "Instrument Serif", "Iowan Old Style", Georgia, serif;
    font-size: 18px;
    color: #5e5c50;
    top: 2px;
    height: 31px;
    background: #f2eddf;
    display: flex;
    align-items: center;
}

:deep(input#amount_other_field) {
    height: 100%;
    display: flex;
    width: 100%;
    border: 1px solid #c9bfa6;
    padding: 0 12px;
    background: #f2eddf !important;
    border: none;
    outline: none;
    font-family: "Instrument Serif", "Iowan Old Style", Georgia, serif;
    font-size: 20px;
    color: #5e5c50;
    width: 100%;
    padding: 0px 0px 0px 6px;
}


:deep(.form-container.ou-donation-form){
    background: none !important;
    padding: 0px;
    box-shadow: none !important;
    margin: 0px;
}
</style>
